import React, { useState, useEffect } from "react";
import { Box, Heading, Text, Stack, Card, CardHeader, CardBody, Spinner, Divider, Flex } from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import axios from "../../axiosConfig";
import { StrengthIcon } from "../../assets/icons";
import { useTranslation } from "react-i18next";
import ReportSurveyHeader from "./ReportSurveyHeader";

const Strengths = ({ isHub }) => {
  const { t } = useTranslation();

  const { organizationSurveyId } = useParams();
  const [strengthData, setStrengthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchStrengths = async () => {
      try {
        const response = await axios.get(`/survey_reports/${organizationSurveyId}/strengths`);
        setStrengthData(response.data);
      } catch (err) {
        setError("Failed to load strengths data.");
      } finally {
        setLoading(false);
      }
    };

    fetchStrengths();
  }, [organizationSurveyId]);

  if (loading) return <Spinner size="xl" />;
  if (error) return <Text color="red.500">{error}</Text>;

  return (
    <Box>
      <ReportSurveyHeader survey={strengthData} />

      <Flex align="center" mb={4}>
        <StrengthIcon />
        <Heading as="h1" size="2xl" color="#a2ba06" ml={3}>
          {t("reports.strengths.title")}
        </Heading>
      </Flex>

      <Text fontSize="xl" color="gray.500" mb={4}>
        {t("reports.strengths.description")}
      </Text>
      <Divider />

      {/* Highlighted Strength Area */}
      <Box bg="#f7fbe2" p={6} borderRadius="md" w="full" mt={4}>
        <Text fontSize="2xl" fontWeight="bold" color="#a2ba06">
          {t(`reports.layout.${strengthData.highest_area.toLowerCase()}`)}
        </Text>
      </Box>

      <Divider />

      {/* Top Two Strength Factors */}
      <Heading as="h3" size="lg" mt={6} textAlign="left">
        {t("reports.strengths.focus_factors")}
      </Heading>
      <Stack spacing={4} w="full">
        {strengthData.top_factors.map((factor, index) => (
          <Flex
            key={index}
            direction={{ base: "column", md: "row" }}
            gap={4}
          >
            <Card flex="1" shadow="md" borderRadius="md" p={4} bg="gray.50">
              <CardHeader>
                <Heading size="md" color="#a2ba06">
                  {factor.factor}
                </Heading>
              </CardHeader>
              <CardBody>
                <Text>{factor.description}</Text>
              </CardBody>
            </Card>
            <Card flex="1" shadow="md" borderRadius="md" p={4} bg="gray.50">
              <CardBody>
                {/* Bullet list section for top 3 questions */}
                {factor.questions && factor.questions.length > 0 && (
                  <Box>
                    <Box as="ul" pl={4}>
                      {factor.questions.slice(0, 3).map((question, qIndex) => (
                        <Box as="li" key={qIndex}>
                          {question.text}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </CardBody>
            </Card>
          </Flex>
        ))}
      </Stack>
    </Box>
  );
};

export default Strengths;
